import { useEffect } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { useResubmissions } from '~/features/dashboard/hooks/useResubmissions';
import { endOfTime, startOfTodayUtc } from '~/features/dashboard/utils/resubmissionDateRange';
import { queryKeys } from '~/utils/queryKeys';

// setTimeout overflows (fires immediately) above a signed 32-bit delay
const MAX_TIMEOUT_MS = 2 ** 31 - 1;
// the backend bounds "due" at its own clock (exclusive) — refetch a moment later to absorb clock skew
const DUE_BUFFER_MS = 1_000;

// resubmissions turn due by clock alone (no event/push) - refetch when the earliest is reached, which reschedules
export const useResubmissionDueTimer = () => {
    const queryClient = useQueryClient();
    const { data, dataUpdatedAt } = useResubmissions({ end: endOfTime(), start: startOfTodayUtc() });

    useEffect(() => {
        const now = Date.now();
        const upcoming = (data ?? [])
            .map(({ resubmission }) => resubmission)
            .filter((resubmission): resubmission is number => resubmission !== null && resubmission > now);
        if (upcoming.length === 0) return undefined;

        const delay = Math.min(Math.min(...upcoming) - now + DUE_BUFFER_MS, MAX_TIMEOUT_MS);
        const timeout = setTimeout(() => {
            queryClient.invalidateQueries({ queryKey: queryKeys.getResubmissionLoanRequestsByDate() });
        }, delay);

        return () => clearTimeout(timeout);
    }, [data, dataUpdatedAt, queryClient]);
};
